import React from "react";
import { Link } from "react-router-dom";
import { ChevronRightIcon } from "@heroicons/react/24/outline";
import { cn } from "../../DashboardPage/lib/utils";

export interface BreadcrumbItem {
  label: string;
  /** Omit on the current (last) item so it renders as plain, non-clickable text. */
  to?: string;
}

/**
 * Every trail starts here — the Dashboard is the home page for user, admin and
 * DSP accounts alike. On the Dashboard itself use `{ label: "Dashboard" }`.
 */
export const HOME_CRUMB: BreadcrumbItem = { label: "Dashboard", to: "/dashboard" };

interface BreadcrumbsProps {
  items: BreadcrumbItem[];
  className?: string;
}

/**
 * Page-hierarchy trail: each item with a `to` is a link, the last item (the current
 * page) is plain text. Items are separated by a chevron.
 */
export default function Breadcrumbs({ items, className }: BreadcrumbsProps) {
  return (
    <nav aria-label="Breadcrumb" className={cn("mb-4", className)}>
      <ol className="flex flex-wrap items-center gap-1.5 text-sm text-neutral-500">
        {items.map((item, index) => {
          const isLast = index === items.length - 1;
          return (
            <li key={`${item.label}-${index}`} className="flex items-center gap-1.5">
              {item.to && !isLast ? (
                <Link
                  to={item.to}
                  className="transition-colors rounded hover:text-primary-700 focus:outline-none focus-visible:text-primary-700"
                >
                  {item.label}
                </Link>
              ) : (
                <span
                  className={isLast ? "font-medium text-black" : undefined}
                  aria-current={isLast ? "page" : undefined}
                >
                  {item.label}
                </span>
              )}
              {!isLast && (
                <ChevronRightIcon className="w-4 h-4 shrink-0 text-neutral-400" />
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
